/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    Component,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { IRspContentRewriteRule } from 'generated-types';
import { RspContentRewriteRule } from 'object-types';

import {
    MessageItem,
    RepeatedMessageItem,
    TMessageItem,
} from 'ajs/modules/data-model/factories';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import { SearchReplacePairConfigItem } from './search-replace-pair.config-item.factory';
import * as l10n from './rsp-content-rewrite-rule.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Partial type of RSP Content Rewrite Rule.
 */
type TRspContentRewriteRulePartial = Omit<IRspContentRewriteRule, 'pairs'>;

/**
 * RSP Content Rewrite Rule Config type.
 */
interface IRspContentRewriteRuleConfig extends TRspContentRewriteRulePartial {
    pairs: RepeatedMessageItem<SearchReplacePairConfigItem>;
}

/**
 * @description
 *
 *   Rsp Content Rewrite Rule Message Item.
 *
 * @author Sarthak kapoor
 */
export class RspContentRewriteRuleConfigItem extends
    withEditChildMessageItemMixin<IRspContentRewriteRuleConfig, TMessageItem>(
        withFullModalMixin(MessageItem),
    )<IRspContentRewriteRuleConfig> {
    public static ngDependencies = [L10nService];

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: RspContentRewriteRule,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getNgDependency(L10nService);
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Add Search Replace String pair.
     */
    public addSearchReplaceStringRow(): void {
        this.addChildMessageItem({
            field: 'pairs',
        });
    }

    /**
     * Edit the selected Search Replace String pair.
     */
    public editSearchReplaceStringRow(searchReplacePair: SearchReplacePairConfigItem): void {
        this.editChildMessageItem({
            field: 'pairs',
            messageItem: searchReplacePair,
            modalBindings: {
                isEditing: true,
            },
        });
    }

    /**
     * Remove the selected Search Replace String pair.
     */
    public deleteSearchReplaceStringRow(pair: SearchReplacePairConfigItem): void {
        this.config.pairs.removeByMessageItem(pair);
    }

    /**
     * Return the Search Replace String pair count.
     */
    public get searchReplaceStringPairCount(): number {
        return this.config.pairs.count;
    }

    /**
     * Import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            VsContentRewriteRuleModalComponent,
        } = await import(
            /* webpackChunkName: "virtual-service-modal" */
            /* eslint-disable-next-line max-len */
            'ng/lazy-loaded-components/modals/virtual-service-modal/vs-content-rewrite-rules/vs-content-rewrite-rule-modal/vs-content-rewrite-rule-modal.component'
        );

        return VsContentRewriteRuleModalComponent as Type<Component>;
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.responseContentRewriteRuleBreadCrumbTitle);
    }
}
